import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { saveAdminSession, getAdminSession } from '@/lib/auth';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, ArrowLeft } from 'lucide-react';
import { ADMIN_LOGIN } from '@/constants/testIds';
import { BrandLogo } from '@/components/BrandLogo';

export default function AdminLoginPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [form, setForm] = useState({ email: '', password: '' });
    const [loading, setLoading] = useState(false);
    const from = location.state?.from?.pathname || '/admin';

    if (getAdminSession()) {
        navigate(from, { replace: true });
    }

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post('/admin/login', form);
            saveAdminSession(data.token, data.admin);
            toast.success(`Welcome, ${data.admin.name}!`);
            navigate(from, { replace: true });
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-brand-orb px-4 py-10 sm:px-6">
            <div className="pointer-events-none absolute -top-32 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-blob-lavender" />
            <div className="pointer-events-none absolute -bottom-24 right-0 h-96 w-96 rounded-full bg-blob-periwinkle blur-3xl" />

            <div className="relative z-10 w-full max-w-md">
                <div className="mb-8 text-center">
                    <BrandLogo size="lg" to="/" className="mx-auto" />
                    <p className="mt-3 text-sm text-muted-foreground">Admin console</p>
                </div>
                <Card className="border-border/60 shadow-[0_20px_60px_rgba(12,12,44,0.08)]">
                    <CardContent className="p-8">
                        <div className="mb-4 flex items-center gap-2 rounded-full border border-accent/30 bg-secondary px-3 py-1 text-xs font-medium text-primary w-fit">
                            <ShieldCheck className="h-3.5 w-3.5 text-accent" />
                            Role-based access
                        </div>
                        <h1 className="font-display text-2xl font-semibold tracking-tight text-primary">Admin login</h1>
                        <p className="mt-1 text-sm text-muted-foreground">Sign in to manage bookings, mentors and students.</p>
                        <form onSubmit={submit} className="mt-6 space-y-4">
                            <div>
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                                    data-testid={ADMIN_LOGIN.email}
                                />
                            </div>
                            <div>
                                <Label htmlFor="password">Password</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    required
                                    value={form.password}
                                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                                    data-testid={ADMIN_LOGIN.password}
                                />
                            </div>
                            <Button type="submit" size="lg" className="w-full" disabled={loading} data-testid={ADMIN_LOGIN.submit}>
                                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Sign in
                            </Button>
                        </form>
                    </CardContent>
                </Card>
                <Link to="/" className="mt-6 flex items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="h-3 w-3" /> Back to student sign in
                </Link>
            </div>
        </div>
    );
}
